import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

/* ===========================================================================
 * NotificationIntegrations — "Automações" (Webhooks de Saída).
 *
 * O cliente cria o receptor de webhook DENTRO da própria plataforma dele
 * (Reportana, Mex10, SMS Funnel, ActiveCampaign, Klaviyo, SAK), copia a URL
 * que a plataforma deu, e cola aqui. Quando um código é gerado (por qualquer
 * checkout conectado), o COMEX manda nome, endereço, código e link de
 * rastreio pra essa URL.
 *
 * Backend: /api/notifications/{webhooks, webhooks/connect, webhooks/:id,
 *          webhooks/:id/logs, webhooks/:id/test}
 *
 * Segue o padrão visual compartilhado .ipg-* (ver integration-pages.css).
 * ========================================================================= */

// Logo real de cada plataforma via favicon oficial do domínio (mesma técnica
// usada na tela de Checkouts) — com fallback automático pro ícone genérico.
const favicon = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

const PLATFORM_META = {
  reportana: { label: 'Reportana', icon: 'fa-comment-dots', color: '#6366f1', logoUrl: favicon('reportana.com') },
  mex10: { label: 'Mex10', icon: 'fa-mobile-screen', color: '#0ea5e9', logoUrl: favicon('mex10.com') },
  smsfunnel: { label: 'SMS Funnel', icon: 'fa-paper-plane', color: '#f43f5e', logoUrl: favicon('smsfunnel.com.br') },
  activecampaign: { label: 'ActiveCampaign', icon: 'fa-envelope-open-text', color: '#004dc1', logoUrl: favicon('activecampaign.com') },
  klaviyo: { label: 'Klaviyo', icon: 'fa-bullhorn', color: '#1a1a1a', logoUrl: favicon('klaviyo.com') },
  sak: { label: 'SAK', icon: 'fa-link', color: '#16a34a', logoUrl: favicon('sak.com.br') },
};

/** Ícone da plataforma: usa o logo real (favicon oficial) quando disponível,
 * e cai pro ícone genérico automaticamente se a imagem falhar ao carregar. */
const PlatformIcon = ({ meta }) => {
  const [failed, setFailed] = useState(false);
  if (meta.logoUrl && !failed) {
    return (
      <div className="ipg-type-icon" style={{ background: '#fff' }}>
        <img src={meta.logoUrl} alt={meta.label} onError={() => setFailed(true)} style={{ width: 22, height: 22, objectFit: 'contain' }} />
      </div>
    );
  }
  return (
    <div className="ipg-type-icon" style={{ background: meta.color }}>
      <i className={`fas ${meta.icon}`}></i>
    </div>
  );
};

const NotificationIntegrations = () => {
  const { user } = useAuth();
  const credits = user?.credits ?? 0;

  const [data, setData] = useState({ webhooks: [], platforms: {}, maxPerPlatform: 3 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [logsByWebhook, setLogsByWebhook] = useState({});
  const [testingId, setTestingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications/webhooks');
      setData(r.data);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao carregar integrações');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const typesToShow = Object.keys(PLATFORM_META);
  const webhooksByPlatform = typesToShow.reduce((acc, t) => {
    acc[t] = data.webhooks.filter((w) => w.platform === t);
    return acc;
  }, {});

  const toggleExpand = async (webhook) => {
    if (expandedId === webhook.id) { setExpandedId(null); return; }
    setExpandedId(webhook.id);
    try {
      const r = await api.get(`/notifications/webhooks/${webhook.id}/logs`);
      setLogsByWebhook((prev) => ({ ...prev, [webhook.id]: r.data.logs || [] }));
    } catch (err) {
      showToast('error', 'Erro ao carregar logs');
    }
  };

  const handleDisconnect = async (webhook) => {
    if (!window.confirm(`Desconectar "${webhook.name || PLATFORM_META[webhook.platform].label}"? O histórico de envios é mantido.`)) return;
    try {
      await api.delete(`/notifications/webhooks/${webhook.id}`);
      showToast('success', 'Integração desconectada');
      load();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao desconectar');
    }
  };

  const handleTest = async (webhook) => {
    setTestingId(webhook.id);
    try {
      await api.post(`/notifications/webhooks/${webhook.id}/test`);
      showToast('success', 'Teste enviado com sucesso! A plataforma respondeu OK.');
      const r = await api.get(`/notifications/webhooks/${webhook.id}/logs`);
      setLogsByWebhook((prev) => ({ ...prev, [webhook.id]: r.data.logs || [] }));
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Falha no teste — veja o log de diagnóstico');
    } finally {
      setTestingId(null);
    }
  };

  // Reenvia o MESMO pacote que foi (ou tentou ser) enviado — o cliente conserta
  // a URL na plataforma e manda de novo. Sempre disponível: "Enviar" nos que
  // falharam, "Reenviar" nos que já foram (por decisão de produto).
  const handleResend = async (webhook, log) => {
    setResendingId(log.id);
    try {
      await api.post(`/notifications/webhooks/${webhook.id}/logs/${log.id}/resend`);
      showToast('success', log.success ? 'Reenviado com sucesso!' : 'Enviado com sucesso!');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Falha ao reenviar — veja o log');
    } finally {
      setResendingId(null);
      try {
        const r = await api.get(`/notifications/webhooks/${webhook.id}/logs`);
        setLogsByWebhook((prev) => ({ ...prev, [webhook.id]: r.data.logs || [] }));
      } catch (_) { /* mantém a lista como está */ }
    }
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />

      <div className="ipg">
        <header className="ipg-header">
          <div className="ipg-header-inner">
            <div>
              <h1 className="ipg-header-title"><i className="fas fa-paper-plane"></i> Automações</h1>
              <p className="ipg-header-sub">Envie automaticamente nome, endereço e código de rastreio pras suas plataformas de notificação</p>
            </div>
            <div className={`ipg-balance-chip ${credits === 0 ? 'empty' : credits < 10 ? 'low' : ''}`}>
              <i className="fas fa-coins"></i>
              <div className="ipg-balance-info">
                <span className="ipg-balance-label">Créditos</span>
                <span className="ipg-balance-val">{credits}</span>
              </div>
            </div>
          </div>
        </header>

        {toast && (
          <div className={`ipg-toast ipg-toast-${toast.type}`}>
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            <span>{toast.msg}</span>
          </div>
        )}

        <main className="ipg-main">
          <div className="ipg-type-card" style={{ background: 'rgba(99,102,241,0.06)', borderColor: 'rgba(99,102,241,0.2)' }}>
            <div className="ipg-field-row-inline" style={{ color: 'inherit', opacity: 0.85 }}>
              <span><i className="fas fa-circle-info"></i> Como funciona: crie o receptor de webhook dentro da plataforma (Reportana, Mex10, etc.), copie a URL que ela te deu, e cole aqui. Quando um código é gerado, nós mandamos os dados pra essa URL.</span>
            </div>
          </div>

          {loading ? (
            <div className="ipg-loading"><div className="ipg-spinner"></div> Carregando…</div>
          ) : (
            typesToShow.map((platform) => {
              const meta = PLATFORM_META[platform];
              const list = webhooksByPlatform[platform] || [];
              const isConnecting = connectingPlatform === platform;
              return (
                <section key={platform} className="ipg-type-card">
                  <div className="ipg-type-head">
                    <PlatformIcon meta={meta} />
                    <div className="ipg-type-info">
                      <strong>{meta.label}</strong>
                      <span>{list.length}/{data.maxPerPlatform} conectadas</span>
                    </div>
                    {list.length < data.maxPerPlatform && (
                      <button className="ipg-btn-add" onClick={() => setConnectingPlatform(isConnecting ? null : platform)}>
                        <i className="fas fa-plus"></i> Conectar
                      </button>
                    )}
                  </div>

                  {isConnecting && (
                    <ConnectForm
                      platform={platform}
                      onCancel={() => setConnectingPlatform(null)}
                      onConnected={() => { setConnectingPlatform(null); load(); showToast('success', `${meta.label} conectado!`); }}
                      showToast={showToast}
                    />
                  )}

                  {list.length > 0 && (
                    <div className="ipg-store-list">
                      {list.map((wh) => (
                        <div key={wh.id} className="ipg-store-row">
                          <div className="ipg-store-row-head" onClick={() => toggleExpand(wh)}>
                            <span className={`ipg-health-dot ${!wh.active ? 'off' : wh.last_error_message ? 'error' : 'ok'}`}></span>
                            <span className="ipg-store-name">{wh.name || `Integração #${wh.id}`}</span>
                            <span className={`ipg-badge ${wh.active ? 'on' : 'off'}`}>{wh.active ? 'Ativa' : 'Desconectada'}</span>
                            <i className={`fas fa-chevron-${expandedId === wh.id ? 'up' : 'down'}`}></i>
                          </div>

                          {expandedId === wh.id && (
                            <div className="ipg-store-details">
                              <div className="ipg-field-row">
                                <label>URL de destino</label>
                                <div className="ipg-url-box">
                                  <code>{wh.destination_url_masked}</code>
                                </div>
                                <small>A URL completa não é exibida de novo por segurança (pode conter token). Pra trocar, desconecte e conecte de novo.</small>
                              </div>
                              {wh.last_sent_at && (
                                <div className="ipg-field-row-inline">
                                  <span><i className="fas fa-clock"></i> Último envio: {new Date(wh.last_sent_at).toLocaleString('pt-BR')}</span>
                                </div>
                              )}
                              {wh.last_error_message && (
                                <div className="ipg-field-row-inline">
                                  <span style={{ color: '#dc2626' }}><i className="fas fa-triangle-exclamation"></i> Último erro: {wh.last_error_message}</span>
                                </div>
                              )}

                              <div className="ipg-form-actions" style={{ justifyContent: 'flex-start' }}>
                                <button className="ipg-btn-ghost" disabled={testingId === wh.id} onClick={() => handleTest(wh)}>
                                  {testingId === wh.id ? 'Enviando…' : <><i className="fas fa-vial"></i> Testar envio</>}
                                </button>
                                {wh.active && (
                                  <button className="ipg-btn-danger" onClick={() => handleDisconnect(wh)}>
                                    <i className="fas fa-link-slash"></i> Desconectar
                                  </button>
                                )}
                              </div>

                              <div className="ipg-section">
                                <h4><i className="fas fa-list"></i> Log de diagnóstico (últimos envios)</h4>
                                {(logsByWebhook[wh.id] || []).length === 0 ? (
                                  <p className="ipg-empty-mini">Nenhum envio ainda — use "Testar envio" ou gere um código de verdade.</p>
                                ) : (
                                  (logsByWebhook[wh.id] || []).slice(0, 15).map((log) => (
                                    <div key={log.id} className={`ipg-log-row ${log.success ? 'ok' : 'error'}`}>
                                      <span className="ipg-log-time">{new Date(log.created_at).toLocaleString('pt-BR')}</span>
                                      <span className="ipg-log-event">{log.event_type || '—'}</span>
                                      <span className="ipg-log-msg">{log.error_message || (log.success ? `OK (HTTP ${log.response_status})` : '—')}</span>
                                      <button
                                        className="ipg-log-resend"
                                        onClick={() => handleResend(wh, log)}
                                        disabled={resendingId === log.id}
                                        title={log.success ? 'Enviar este pacote de novo' : 'Tentar enviar este pacote agora'}
                                      >
                                        {resendingId === log.id
                                          ? 'Enviando…'
                                          : (log.success ? 'Reenviar' : 'Enviar')}
                                      </button>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </main>
      </div>
    </>
  );
};

/* ===================================================================== */
const ConnectForm = ({ platform, onCancel, onConnected, showToast }) => {
  const [name, setName] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!destinationUrl.trim()) {
      showToast('error', 'Cole a URL do webhook que você copiou da plataforma');
      return;
    }
    setSaving(true);
    try {
      await api.post('/notifications/webhooks/connect', { platform, name: name.trim(), destinationUrl: destinationUrl.trim() });
      onConnected();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao conectar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ipg-connect-form" onSubmit={submit}>
      <div className="ipg-field">
        <label>Nome (apelido, opcional)</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: WhatsApp via Reportana" />
      </div>
      <div className="ipg-field">
        <label>URL do webhook (copiada da plataforma)</label>
        <input value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} placeholder="https://..." />
        <small>Crie o receptor de webhook dentro da {PLATFORM_META[platform].label} e cole a URL que ela te der aqui.</small>
      </div>
      <div className="ipg-form-actions">
        <button type="button" className="ipg-btn-ghost" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="ipg-btn-primary" disabled={saving}>
          {saving ? 'Conectando…' : 'Conectar'}
        </button>
      </div>
    </form>
  );
};

export default NotificationIntegrations;
